import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useProfile } from "../hooks/useProfile";
import { useRolodex } from "../hooks/useRolodex";
import { useDiscoveryGraph } from "../hooks/useDiscoveryGraph";
import Nav from "../components/Nav";
import ProfileCard from "../components/ProfileCard";
import ContextSwitcher from "../components/ContextSwitcher";
import { buildConnectionTree } from "../lib/branching";
import { buildContextGraph } from "../lib/contextGraph";
import { entriesForContext } from "../lib/facetForm";
import { filterEntriesByContext } from "../lib/rolodexContext";
import NetworkGraph from "../components/NetworkGraph";
import AddConnectionModal from "../components/AddConnectionModal";

function initialContext() {
  const saved = window.localStorage.getItem("aline:rolodex-context");
  return ["professional", "personal", "all"].includes(saved) ? saved : "professional";
}

function mergeCandidateNodes(privateNodes, publicNodes) {
  const byId = new Map();
  [...(privateNodes || []), ...(publicNodes || [])].forEach((node) => {
    if (!node?.id || byId.has(node.id)) return;
    byId.set(node.id, node);
  });
  return [...byId.values()];
}

export default function Dashboard() {
  const { user } = useAuth();
  const { profile } = useProfile(user?.uid);
  const { entries, loading, error } = useRolodex(user?.uid);
  const discoveryGraph = useDiscoveryGraph(user?.uid, entries);
  const [searchParams] = useSearchParams();
  const [context, setContext] = useState(initialContext);
  const navigate = useNavigate();
  const [showAddModal, setShowAddModal] = useState(false);
  const [collapsed, setCollapsed] = useState(() => new Set());

  const visibleEntries = useMemo(() => filterEntriesByContext(entries, context), [entries, context]);
  const displayConnections = useMemo(
    () => entriesForContext(visibleEntries, context),
    [visibleEntries, context]
  );
  const connectionTree = useMemo(
    () => buildConnectionTree(displayConnections),
    [displayConnections]
  );
  const graph = useMemo(
    () => buildContextGraph(entries, user?.uid, context, profile),
    [entries, user?.uid, context, profile]
  );
  const allPrivateGraph = useMemo(
    () => buildContextGraph(entries, user?.uid, "all", profile),
    [entries, user?.uid, profile]
  );
  const networkNodes = useMemo(
    () => mergeCandidateNodes(allPrivateGraph.nodes, discoveryGraph.nodes),
    [allPrivateGraph.nodes, discoveryGraph.nodes]
  );
  const counts = useMemo(
    () => ({
      professional: filterEntriesByContext(entries, "professional").length,
      personal: filterEntriesByContext(entries, "personal").length,
      all: filterEntriesByContext(entries, "all").length,
    }),
    [entries]
  );

  // The old centre panel is gone: a connection is now its own page. Links that
  // still carry ?connection= (saved URLs, older graph deep links) are forwarded.
  useEffect(() => {
    const requested = searchParams.get("connection");
    if (requested) navigate(`/connections/${encodeURIComponent(requested)}`, { replace: true });
  }, [searchParams, navigate]);

  const openConnection = (entryId) => navigate(`/connections/${entryId}`);

  const changeContext = (nextContext) => {
    setContext(nextContext);
    window.localStorage.setItem("aline:rolodex-context", nextContext);
  };

  const toggleCollapse = (id) => setCollapsed((previous) => {
    const next = new Set(previous);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });


  const renderTreeRows = (nodesList) => nodesList.flatMap((node) => {
    const { connection, children, depth } = node;
    const hasChildren = children.length > 0;
    const isCollapsed = collapsed.has(connection.id);
    const viaName = depth === 0 && connection.branch_parent_uid
      ? connection.branch_parent_name
      : null;
    const row = (
      <div key={connection.id} className="conn-tree-row" style={{ paddingLeft: depth * 16 }}>
        {hasChildren ? (
          <button
            className="conn-caret"
            onClick={() => toggleCollapse(connection.id)}
            aria-label={isCollapsed ? "Expand" : "Collapse"}
          >
            {isCollapsed ? "▸" : "▾"}
          </button>
        ) : <span className="conn-caret-spacer" />}
        <ProfileCard
          connection={connection}
          onSelect={openConnection}
          viaName={viaName}
        />
      </div>
    );
    const childRows = hasChildren && !isCollapsed ? renderTreeRows(children) : [];
    return [row, ...childRows];
  });

  const emptyCopy = context === "personal"
    ? "Your personal rolodex is ready for friends, family, and the people behind your work life."
    : context === "professional"
    ? "Add colleagues, classmates, mentors, and professional introductions."
    : "Add a connection to start mapping both sides of your life.";

  return (
    <>
      <Nav profile={profile} active="connections" />
      <div className="page-wrapper">
        <div className="page-content dashboard-page-content">
          <div className="dashboard-context-bar">
            <div>
              <p className="eyebrow">One rolodex, two parts of your life</p>
              <h1>Your connections</h1>
            </div>
            <ContextSwitcher value={context} onChange={changeContext} counts={counts} hideLabel />
          </div>

          {error && <div className="error-banner">Your rolodex could not be loaded. Refresh to try again.</div>}

          <div className="dashboard-layout is-two-column">
            <div className="sidebar-left panel-card">
              <div className="panel-header">
                <div className="panel-header-left">
                  <p className="eyebrow">{context === "all" ? "All connections" : `${context} rolodex`}</p>
                  <h3 className="panel-title">Connections</h3>
                </div>
                <span className="count-badge">{visibleEntries.length}</span>
              </div>
              <div className="connection-list">
                {loading ? (
                  <div className="loading-center"><div className="spinner" /></div>
                ) : visibleEntries.length === 0 ? (
                  <div className="context-empty-state">
                    <p>{emptyCopy}</p>
                    <button onClick={() => setShowAddModal(true)}>Add a connection</button>
                  </div>
                ) : renderTreeRows(connectionTree)}
              </div>
              <div className="connection-list-add">
                <button className="btn-primary connection-add-button" onClick={() => setShowAddModal(true)}>
                  + Add connection
                </button>
              </div>
            </div>


            <div className="sidebar-right panel-card context-map-panel">
              <div className="context-map-header">
                <div>
                  <p className="eyebrow">{context} map</p>
                  <span>{graph.nodes.length - 1} people</span>
                </div>
                <div className="context-map-key" aria-label="Map legend">
                  {(context === "professional" || context === "all") && <span className="key-professional">Professional</span>}
                  {(context === "personal" || context === "all") && <span className="key-personal">Personal</span>}
                </div>
              </div>
              <NetworkGraph nodes={graph.nodes} edges={graph.edges} />
            </div>
          </div>
        </div>
      </div>

      {showAddModal && (
        <AddConnectionModal
          currentUser={user}
          entries={entries}
          initialContext={context === "all" ? "both" : context}
          networkNodes={networkNodes}
          onClose={() => setShowAddModal(false)}
        />
      )}
    </>
  );
}
